import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, ChevronDown, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { NAV_LINKS } from "../../data/content";
import { scrollToId } from "../../lib/scroll";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "../ui/sheet";

function LanguageSelector() {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                type="button"
                data-testid="language-selector"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-1.5 border border-white/15 px-3 py-2 text-xs font-semibold tracking-widest text-slate-200 transition-colors hover:border-gold/60 hover:text-gold"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                EN
                <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-44 border border-white/10 bg-navy-surface shadow-2xl shadow-black/50"
                    role="listbox"
                    data-testid="language-selector-menu"
                >
                    <div className="flex items-center justify-between px-4 py-2.5 text-xs text-ivory">
                        English
                        <Check size={13} className="text-gold" />
                    </div>
                    {["Français", "Tiếng Việt"].map((l) => (
                        <div key={l} className="flex items-center justify-between px-4 py-2.5 text-xs text-slate-500">
                            {l}
                            <span className="text-[10px] uppercase tracking-widest text-gold/60">Soon</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24 });

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNav = (e, link) => {
        e.preventDefault();
        if (link.route) {
            navigate(link.href);
            return;
        }
        if (link.placeholder) {
            toast("Insights launches soon — market notes and healthcare guidance from our international team.");
            return;
        }
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToId(link.href), 500);
            return;
        }
        scrollToId(link.href);
    };

    const goContact = () => {
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToId("#contact"), 500);
        } else {
            scrollToId("#contact");
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
                scrolled ? "border-white/10 bg-[#060D1A]/90" : "border-white/5 bg-[#060D1A]/70"
            }`}
        >
            <div className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between gap-4 px-5 lg:px-10">
                <a
                    href="#home"
                    data-testid="header-logo-link"
                    onClick={(e) => {
                        e.preventDefault();
                        if (location.pathname !== "/") {
                            navigate("/");
                        } else {
                            scrollToId("#home");
                        }
                    }}
                    className="inline-flex items-center rounded-[3px] bg-ivory px-3 py-2"
                    aria-label="Viet Medica Ltd — home"
                >
                    <img
                        src="/assets/logos/viet-medica-logo.png"
                        alt="Viet Medica Ltd — official logo"
                        className="h-8 w-auto"
                    />
                </a>

                <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
                    {NAV_LINKS.slice(1).map((link) => (
                        <a
                            key={link.label}
                            href={link.href}
                            data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            onClick={(e) => handleNav(e, link)}
                            className="whitespace-nowrap text-[12.5px] font-medium tracking-wide text-slate-300 transition-colors hover:text-gold"
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <LanguageSelector />
                    <button
                        type="button"
                        data-testid="speak-to-our-team-button"
                        onClick={goContact}
                        className="hidden items-center gap-2 bg-gold px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-navy-deep transition-colors hover:bg-gold-bright md:inline-flex"
                    >
                        Speak to Our Team
                        <ArrowUpRight size={14} />
                    </button>

                    <Sheet>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                data-testid="mobile-menu-button"
                                className="inline-flex items-center justify-center border border-white/15 p-2.5 text-ivory transition-colors hover:border-gold/60 hover:text-gold xl:hidden"
                                aria-label="Open menu"
                            >
                                <Menu size={18} />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[86vw] border-l border-white/10 bg-navy-deep p-0 sm:max-w-sm">
                            <div className="flex h-full flex-col px-7 pb-8 pt-14">
                                <span className="text-[11px] font-semibold uppercase tracking-[0.4em] text-gold">Menu</span>
                                <nav className="mt-6 flex flex-col" aria-label="Mobile">
                                    {NAV_LINKS.map((link, i) => (
                                        <SheetClose asChild key={link.label}>
                                            <a
                                                href={link.href}
                                                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                                onClick={(e) => handleNav(e, link)}
                                                className="border-b border-white/8 py-4 font-serif text-2xl font-light text-ivory transition-colors hover:text-gold"
                                            >
                                                <span className="mr-3 text-xs text-gold/70">0{i + 1}</span>
                                                {link.label}
                                            </a>
                                        </SheetClose>
                                    ))}
                                </nav>
                                <SheetClose asChild>
                                    <button
                                        type="button"
                                        data-testid="mobile-speak-to-team-button"
                                        onClick={goContact}
                                        className="mt-8 inline-flex items-center justify-center gap-2 bg-gold px-5 py-3.5 text-xs font-bold uppercase tracking-widest text-navy-deep transition-colors hover:bg-gold-bright"
                                    >
                                        Speak to Our Team
                                        <ArrowUpRight size={14} />
                                    </button>
                                </SheetClose>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
            <motion.div
                className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-gold"
                style={{ scaleX: progress }}
                aria-hidden="true"
            />
        </header>
    );
}
